/* eslint-disable no-undef */
/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/**
 * This component is responsible for drawing the MPT Flight Path Service Alert Markers
 */
import { FeatureCollection } from '@turf/helpers';
import { concat, first, isNil, last, size } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import FlightLeg from '../../../shared/entities/flightLeg';
import FlightLegServiceAlerts, { ServiceAlert, Waypoint } from '../../../shared/entities/flightLegServiceAlerts';
import {
  selectMissionPlanLandingMarker,
  selectMissionPlanPathMarkers,
  selectMissionPlanTakeoffMarker,
} from '../../../store';
import {
  HNA_ALL_ALTITUDE_RESTRICTION_ALTITUDE_FT,
  ServiceAlertType,
  getAltitudeRestrictionForBeam,
} from '../../../utils/MptFlightPathSegmentingUtils';
import { MapActionsContext } from '../../common/map/BaseMapContainer';
import MarkerDialogContent, { ServiceAlertEventType } from './MarkerDialogContent';
import ServiceAlertDialog from './ServiceAlertDialog';

// Get the google map instance provided by the AircraftFleetMapContainer context
export interface ServiceAlertsContainerProps {
  map: google.maps.Map;
  flightLeg: FlightLeg;
  flightLegServiceAlerts: { [flightLegId: number]: FlightLegServiceAlerts };
  isRequestingFlightLegServiceAlerts?: boolean;
  overlaidFlightLegHidden?: boolean;

  setHoveredBeam: (beamName: string | null) => {};
  mapId: string;
}

const MptServiceAlertsContainer = ({
  flightLegServiceAlerts,
  flightLeg,
  overlaidFlightLegHidden,
  setHoveredBeam,
  isRequestingFlightLegServiceAlerts,
  map,
  mapId,
}: ServiceAlertsContainerProps) => {
  const dispatch = useDispatch();
  const takeoffMarker = useSelector(selectMissionPlanTakeoffMarker(mapId), shallowEqual);
  const landingMarker = useSelector(selectMissionPlanLandingMarker(mapId), shallowEqual);
  const flightPathMarkers = useSelector(selectMissionPlanPathMarkers(mapId), shallowEqual);

  const mapActionsContext = useContext(MapActionsContext);
  const { setIsServiceAlertDialogOpen, unsetIsServiceAlertDialogOpen } = mapActionsContext.serviceAlertsActions;
  const { setMissionPlanTakeoffMarker, setMissionPlanLandingMarker, setMissionPlanPathMarkers } =
    mapActionsContext.mapActions;

  const [serviceAlertMarkers, setServiceAlertMarkers] = useState<google.maps.Marker[]>([]);
  const visibleOverlays = useRef<{ [duration: string]: boolean }>({});
  const [hovering, setHovering] = useState(false);

  // Create flight path markers and save in store
  useEffect(() => {
    if (
      !isNil(flightLeg) &&
      !isNil(flightLeg.id) &&
      !isNil(flightLegServiceAlerts) &&
      !isNil(flightLegServiceAlerts[flightLeg.id]) &&
      size(flightLegServiceAlerts[flightLeg.id].waypoints)
    ) {
      const pathServiceAlertMarkers: google.maps.Marker[] = [];
      flightLegServiceAlerts[flightLeg.id].serviceAlerts
        .filter(
          (serviceAlert: ServiceAlert) =>
            serviceAlert.type !== 'BEFORE_HAND_OFF' && serviceAlert.type !== 'AFTER_HAND_OFF',
        )
        .forEach((serviceAlert: ServiceAlert) => {
          let markerImage = getMarkerImageForServiceAlert(serviceAlert);

          if (markerImage) {
            let serviceAlertMarker = new google.maps.Marker({
              position: new google.maps.LatLng(serviceAlert.lat, serviceAlert.lon),
              icon: { url: markerImage, scaledSize: new google.maps.Size(24, 24) },
            });
            pathServiceAlertMarkers.push(serviceAlertMarker);
            visibleOverlays.current[serviceAlert.id] = false;
            serviceAlertMarker.addListener('mouseover', () => {
              visibleOverlays.current[serviceAlert.id] = true;
              dispatch(setIsServiceAlertDialogOpen());
              setHoveredBeam(null);
              setHovering(true);
            });

            serviceAlertMarker.addListener('mouseout', () => {
              visibleOverlays.current = {};
              dispatch(unsetIsServiceAlertDialogOpen());
              setHovering(false);
            });
          }
        });

      dispatch(setMissionPlanPathMarkers(pathServiceAlertMarkers));
    }
  }, [
    flightLegServiceAlerts,
    flightLeg.id,
    visibleOverlays,
    dispatch,
    setHoveredBeam,
    setMissionPlanPathMarkers,
    unsetIsServiceAlertDialogOpen,
    setIsServiceAlertDialogOpen,
    flightLeg,
  ]);

  // Creates takeoffMarker and saves in store
  useEffect(() => {
    if (
      !isNil(flightLeg.id) &&
      !isNil(flightLegServiceAlerts) &&
      !isNil(flightLegServiceAlerts[flightLeg.id]) &&
      size(flightLegServiceAlerts[flightLeg.id].waypoints)
    ) {
      const originWaypoint: Waypoint | undefined = first(flightLegServiceAlerts[flightLeg.id].waypoints);
      if (!isNil(originWaypoint)) {
        const takeoffMarker = new google.maps.Marker({
          position: new google.maps.LatLng(originWaypoint.latitude, originWaypoint.longitude),
          icon: { url: getTakeoffMarkerSvg(), scaledSize: new google.maps.Size(20, 20) },
        });
        takeoffMarker.addListener('mouseover', () => {
          visibleOverlays.current.takeoff = true;
          setHovering(true);
        });
        takeoffMarker.addListener('mouseout', () => {
          visibleOverlays.current = {};
          setHovering(false);
        });

        dispatch(setMissionPlanTakeoffMarker(takeoffMarker));
      }
    }
  }, [flightLegServiceAlerts, flightLeg.id, dispatch, setMissionPlanTakeoffMarker]);

  // Creates landingMarker and saves in store
  useEffect(() => {
    if (
      !isNil(flightLeg.id) &&
      !isNil(flightLegServiceAlerts) &&
      !isNil(flightLegServiceAlerts[flightLeg.id]) &&
      size(flightLegServiceAlerts[flightLeg.id].waypoints)
    ) {
      const destinationWaypoint: Waypoint | undefined = last(flightLegServiceAlerts[flightLeg.id].waypoints);
      if (!isNil(destinationWaypoint)) {
        const mapLandingMarker = new google.maps.Marker({
          position: new google.maps.LatLng(destinationWaypoint.latitude, destinationWaypoint.longitude),
          icon: { url: getLandingMarkerSvg(), scaledSize: new google.maps.Size(20, 20) },
        });
        mapLandingMarker.addListener('mouseover', () => {
          visibleOverlays.current.landing = true;
          setHovering(true);
        });
        mapLandingMarker.addListener('mouseout', () => {
          visibleOverlays.current = {};
          setHovering(false);
        });

        dispatch(setMissionPlanLandingMarker(mapLandingMarker));
      }
    }
  }, [flightLegServiceAlerts, flightLeg.id, dispatch, setMissionPlanLandingMarker]);

  useEffect(() => {
    if (!isNil(flightLeg.id) && !isNil(flightLegServiceAlerts) && !isNil(flightLeg) && !overlaidFlightLegHidden) {
      if (flightLeg) {
        if (
          !isNil(flightLegServiceAlerts) &&
          !isNil(flightLegServiceAlerts[flightLeg.id]) &&
          size(flightLegServiceAlerts[flightLeg.id].waypoints)
        ) {
          let tempServiceAlertMarkers: google.maps.Marker[] = [];
          if (!isNil(takeoffMarker) && !isNil(landingMarker) && !isNil(flightPathMarkers)) {
            tempServiceAlertMarkers = concat(tempServiceAlertMarkers, flightPathMarkers, takeoffMarker, landingMarker);
          }
          setServiceAlertMarkers(tempServiceAlertMarkers);
        }
      }
    } else if (overlaidFlightLegHidden) {
      setServiceAlertMarkers([]);
    }
  }, [
    flightLegServiceAlerts,
    flightLeg,
    landingMarker,
    flightPathMarkers,
    takeoffMarker,
    overlaidFlightLegHidden,
    flightLeg.id,
  ]);

  useEffect(() => {
    serviceAlertMarkers.forEach((serviceAlertMarker: google.maps.Marker) => {
      serviceAlertMarker.setMap(map);
    });

    return () => {
      serviceAlertMarkers.forEach((serviceAlertMarker: google.maps.Marker) => {
        serviceAlertMarker.setMap(null);
      });
    };
  }, [serviceAlertMarkers, map]);

  if (!isNil(flightLeg.id) && !isNil(flightLegServiceAlerts) && !isRequestingFlightLegServiceAlerts && hovering) {
    if (flightLeg) {
      if (!isNil(flightLegServiceAlerts[flightLeg.id])) {
        const selectedServiceAlert = flightLegServiceAlerts[flightLeg.id];
        return concat(
          (selectedServiceAlert?.serviceAlerts || [])
            .filter(
              (serviceAlert: ServiceAlert) =>
                serviceAlert.type !== ServiceAlertType.BEFORE_HAND_OFF &&
                serviceAlert.type !== ServiceAlertType.AFTER_HAND_OFF,
            )
            .filter((serviceAlert: ServiceAlert) => visibleOverlays.current[serviceAlert.id])
            .map((serviceAlert: ServiceAlert) =>
              getServiceAlertDialogComponent(serviceAlert, selectedServiceAlert.beams, map, visibleOverlays.current),
            ),
          // Takeoff ServiceAlertDialog
          <ServiceAlertDialog
            key={0}
            map={map}
            latLng={
              new google.maps.LatLng(
                selectedServiceAlert.waypoints[0].latitude,
                selectedServiceAlert.waypoints[0].longitude,
              )
            }
            veritcalOffsetPx={
              getServiceAlertTypeForTakeoffOrLanding(
                selectedServiceAlert.originBeamType,
                selectedServiceAlert.originBeamNetwork,
                selectedServiceAlert.originBeamMapType,
                selectedServiceAlert.originBeamName,
                selectedServiceAlert.originInCoverage,
                selectedServiceAlert.beams,
              ) === ServiceAlertEventType.ENTER_ALL_ALTITUDE_HNA ||
              getServiceAlertTypeForTakeoffOrLanding(
                selectedServiceAlert.originBeamType,
                selectedServiceAlert.originBeamNetwork,
                selectedServiceAlert.originBeamMapType,
                selectedServiceAlert.originBeamName,
                selectedServiceAlert.originInCoverage,
                selectedServiceAlert.beams,
              ) === ServiceAlertEventType.ENTER_ALTITUDE_RESTRICTED_HNA
                ? 118
                : 104
            }
            visible={visibleOverlays.current['takeoff']}
            childComponent={
              <MarkerDialogContent
                title={`TAKEOFF: ${flightLeg.origin}`}
                isLandingOrTakeoff
                beam={
                  getServiceAlertTypeForTakeoffOrLanding(
                    selectedServiceAlert.originBeamType,
                    selectedServiceAlert.originBeamNetwork,
                    selectedServiceAlert.originBeamMapType,
                    selectedServiceAlert.originBeamName,
                    selectedServiceAlert.originInCoverage,
                    selectedServiceAlert.beams,
                  ) === ServiceAlertEventType.ENTER_NO_COVERAGE
                    ? 'No Coverage'
                    : selectedServiceAlert.originBeamDisplayName
                }
                hnaAltitude={getAltitudeRestrictionForBeam(
                  selectedServiceAlert.originBeamName,
                  selectedServiceAlert.originBeamNetwork,
                  selectedServiceAlert.originBeamMapType,
                  selectedServiceAlert.beams,
                )}
                timeSinceTakeOffMin={0}
                userSetTakeoffTimeMin={selectedServiceAlert.userSetTakeoffTimeMin}
                serviceAlertEventType={getServiceAlertTypeForTakeoffOrLanding(
                  selectedServiceAlert.originBeamType,
                  selectedServiceAlert.originBeamNetwork,
                  selectedServiceAlert.originBeamMapType,
                  selectedServiceAlert.originBeamName,
                  selectedServiceAlert.originInCoverage,
                  selectedServiceAlert.beams,
                )}
              />
            }
          />,
          // Landing ServiceAlertDialog
          <ServiceAlertDialog
            key={selectedServiceAlert.durationMin}
            map={map}
            latLng={
              new google.maps.LatLng(
                selectedServiceAlert.waypoints[selectedServiceAlert.waypoints.length - 1].latitude,
                selectedServiceAlert.waypoints[selectedServiceAlert.waypoints.length - 1].longitude,
              )
            }
            title={`LANDING: ${flightLeg.destination}`}
            veritcalOffsetPx={
              getServiceAlertTypeForTakeoffOrLanding(
                selectedServiceAlert.destinationBeamType,
                selectedServiceAlert.destinationBeamNetwork,
                selectedServiceAlert.destinationBeamMapType,
                selectedServiceAlert.destinationBeamName,
                selectedServiceAlert.destinationInCoverage,
                selectedServiceAlert.beams,
              ) === ServiceAlertEventType.ENTER_ALL_ALTITUDE_HNA ||
              getServiceAlertTypeForTakeoffOrLanding(
                selectedServiceAlert.destinationBeamType,
                selectedServiceAlert.destinationBeamNetwork,
                selectedServiceAlert.destinationBeamMapType,
                selectedServiceAlert.destinationBeamName,
                selectedServiceAlert.destinationInCoverage,
                selectedServiceAlert.beams,
              ) === ServiceAlertEventType.ENTER_ALTITUDE_RESTRICTED_HNA
                ? 118
                : 104
            }
            visible={visibleOverlays.current['landing']}
            childComponent={
              <MarkerDialogContent
                title={`LANDING: ${flightLeg.destination}`}
                hnaAltitude={getAltitudeRestrictionForBeam(
                  selectedServiceAlert.destinationBeamName,
                  selectedServiceAlert.destinationBeamNetwork,
                  selectedServiceAlert.destinationBeamMapType,
                  selectedServiceAlert.beams,
                )}
                isLandingOrTakeoff
                beam={
                  getServiceAlertTypeForTakeoffOrLanding(
                    selectedServiceAlert.destinationBeamType,
                    selectedServiceAlert.destinationBeamNetwork,
                    selectedServiceAlert.destinationBeamMapType,
                    selectedServiceAlert.destinationBeamName,
                    selectedServiceAlert.destinationInCoverage,
                    selectedServiceAlert.beams,
                  ) === ServiceAlertEventType.ENTER_NO_COVERAGE
                    ? 'No Coverage'
                    : selectedServiceAlert.destinationBeamDisplayName
                }
                timeSinceTakeOffMin={selectedServiceAlert.durationMin}
                userSetTakeoffTimeMin={
                  selectedServiceAlert.userSetTakeoffTimeMin && selectedServiceAlert.userSetTakeoffTimeMin > -1 // userSetTakeoffTimeMin is -1 when user hasn't set a takeoff time
                    ? selectedServiceAlert.userSetTakeoffTimeMin + selectedServiceAlert.durationMin
                    : -1
                }
                serviceAlertEventType={getServiceAlertTypeForTakeoffOrLanding(
                  selectedServiceAlert.destinationBeamType,
                  selectedServiceAlert.destinationBeamNetwork,
                  selectedServiceAlert.destinationBeamMapType,
                  selectedServiceAlert.destinationBeamName,
                  selectedServiceAlert.destinationInCoverage,
                  selectedServiceAlert.beams,
                )}
              />
            }
          />,
        );
      }
    }
  }
  return null;
};

export default withTheme(MptServiceAlertsContainer);

const getServiceAlertTypeForTakeoffOrLanding = (
  beamType: string | undefined,
  beamNetworkType: string | undefined,
  beamMapType: string | undefined,
  beamName: string | undefined,
  beamInCoverage: boolean,
  beams: FeatureCollection,
): ServiceAlertEventType => {
  if (!beamInCoverage) {
    return ServiceAlertEventType.ENTER_NO_COVERAGE;
  } else if (
    beamType === ServiceAlertType.HNA_ZONE ||
    beamType === ServiceAlertType.HNA ||
    beamType === ServiceAlertType.KA_HNA ||
    beamType === ServiceAlertType.HCX_HNA
  ) {
    return getAltitudeRestrictionForBeam(beamName, beamNetworkType, beamMapType, beams) === HNA_ALL_ALTITUDE_RESTRICTION_ALTITUDE_FT
      ? ServiceAlertEventType.ENTER_ALL_ALTITUDE_HNA
      : ServiceAlertEventType.ENTER_ALTITUDE_RESTRICTED_HNA;
  } else {
    return ServiceAlertEventType.ENTER_COVERAGE;
  }
};
const generateHnaRestrictionAlertDialogComponent = (
  serviceAlert: ServiceAlert,
  beams: FeatureCollection,
  map: google.maps.Map,
  visibleOverlaysObj: { [duration: string]: boolean },
) => {
  return (
    <ServiceAlertDialog
      key={serviceAlert.id}
      map={map}
      visible={visibleOverlaysObj[serviceAlert.id]}
      childComponent={
        getAltitudeRestrictionForBeam(serviceAlert.loginBeamName, serviceAlert.loginBeamNetwork, serviceAlert.loginBeamMapType, beams) ===
        HNA_ALL_ALTITUDE_RESTRICTION_ALTITUDE_FT ? (
          <MarkerDialogContent
            title={`HNA RESTRICTION: ${serviceAlert.loginBeamDisplayName}`}
            hnaAltitude={HNA_ALL_ALTITUDE_RESTRICTION_ALTITUDE_FT}
            enter={serviceAlert.loginBeamDisplayName}
            exit={
              (serviceAlert.logoutBeamType === ServiceAlertType.HNA ||
                serviceAlert.logoutBeamType === ServiceAlertType.KA_HNA ||
                serviceAlert.logoutBeamType === ServiceAlertType.HCX_HNA) &&
              (serviceAlert.loginBeamType === ServiceAlertType.HNA ||
                serviceAlert.loginBeamType === ServiceAlertType.KA_HNA ||
                serviceAlert.logoutBeamType === ServiceAlertType.HCX_HNA) &&
              serviceAlert.type === ServiceAlertType.NO_COVERAGE &&
              serviceAlert.loginBeamInCoverage
                ? 'No Coverage'
                : serviceAlert.logoutBeamType === ServiceAlertType.HNA ||
                  serviceAlert.logoutBeamType === ServiceAlertType.KA_HNA ||
                  serviceAlert.logoutBeamType === ServiceAlertType.HCX_HNA
                ? serviceAlert.logoutBeamDisplayName
                : serviceAlert.type === ServiceAlertType.NO_COVERAGE
                ? 'No Coverage'
                : serviceAlert.logoutBeamDisplayName
            }
            timeSinceTakeOffMin={serviceAlert.timeSinceTakeOffMin}
            userSetTakeoffTimeMin={serviceAlert.timeSinceUserSetTakeoffMin}
            serviceAlertEventType={ServiceAlertEventType.ENTER_ALL_ALTITUDE_HNA}
          />
        ) : (
          <MarkerDialogContent
            title={`HNA RESTRICTION: ${serviceAlert.loginBeamDisplayName}`}
            hnaAltitude={getAltitudeRestrictionForBeam(serviceAlert.loginBeamName, serviceAlert.loginBeamNetwork, serviceAlert.loginBeamMapType, beams)}
            enter={serviceAlert.loginBeamDisplayName}
            exit={
              serviceAlert.logoutBeamType === ServiceAlertType.HNA ||
              serviceAlert.logoutBeamType === ServiceAlertType.KA_HNA ||
              serviceAlert.logoutBeamType === ServiceAlertType.HCX_HNA
                ? serviceAlert.logoutBeamDisplayName
                : serviceAlert.logoutBeamDisplayName || 'No Coverage'
            }
            timeSinceTakeOffMin={serviceAlert.timeSinceTakeOffMin}
            userSetTakeoffTimeMin={serviceAlert.timeSinceUserSetTakeoffMin}
            serviceAlertEventType={ServiceAlertEventType.ENTER_ALTITUDE_RESTRICTED_HNA}
          />
        )
      }
      latLng={new google.maps.LatLng(serviceAlert.lat, serviceAlert.lon)}
      veritcalOffsetPx={140}
    />
  );
};
const getServiceAlertDialogComponent = (
  serviceAlert: ServiceAlert,
  beams: FeatureCollection,
  map: google.maps.Map,
  visibleOverlaysObj: { [duration: string]: boolean },
): JSX.Element => {
  let serviceAlertDialogComponent: JSX.Element = <div />;

  switch (serviceAlert.type) {
    case 'HNA_ZONE':
    case 'HNA':
    case 'KA_HNA':
      serviceAlertDialogComponent =
        serviceAlert.loginBeamType === ServiceAlertType.HNA ||
        serviceAlert.loginBeamType === ServiceAlertType.KA_HNA ||
        serviceAlert.loginBeamType === ServiceAlertType.HCX_HNA ||
        serviceAlert.loginBeamType === ServiceAlertType.HNA_ZONE ? (
          generateHnaRestrictionAlertDialogComponent(serviceAlert, beams, map, visibleOverlaysObj)
        ) : (
          <ServiceAlertDialog
            key={serviceAlert.timeSinceTakeOffMin}
            map={map}
            visible={visibleOverlaysObj[serviceAlert.id]}
            childComponent={
              <MarkerDialogContent
                title={'ENTER COVERAGE'}
                exit={serviceAlert.logoutBeamDisplayName}
                enter={serviceAlert.loginBeamDisplayName}
                timeSinceTakeOffMin={serviceAlert.timeSinceTakeOffMin}
                userSetTakeoffTimeMin={serviceAlert.timeSinceUserSetTakeoffMin}
                serviceAlertEventType={ServiceAlertEventType.ENTER_COVERAGE}
              />
            }
            latLng={new google.maps.LatLng(serviceAlert.lat, serviceAlert.lon)}
            veritcalOffsetPx={124}
          />
        );
      break;
    case 'HAND_OFF':
      serviceAlertDialogComponent = (
        <ServiceAlertDialog
          key={serviceAlert.timeSinceTakeOffMin}
          map={map}
          visible={visibleOverlaysObj[serviceAlert.id]}
          childComponent={
            <MarkerDialogContent
              title={`${serviceAlert.logoutBeamType === serviceAlert.loginBeamType ? 'BEAM' : 'NETWORK'} HANDOFF`}
              exit={serviceAlert.logoutBeamDisplayName}
              enter={serviceAlert.loginBeamDisplayName}
              timeSinceTakeOffMin={serviceAlert.timeSinceTakeOffMin}
              userSetTakeoffTimeMin={serviceAlert.timeSinceUserSetTakeoffMin}
              serviceAlertEventType={ServiceAlertEventType.HAND_OFF}
            />
          }
          latLng={new google.maps.LatLng(serviceAlert.lat, serviceAlert.lon)}
          veritcalOffsetPx={124}
        />
      );

      break;

    case 'NO_COVERAGE':
      if (
        serviceAlert.loginBeamInCoverage &&
        (serviceAlert.loginBeamType === ServiceAlertType.HNA ||
          serviceAlert.loginBeamType === ServiceAlertType.KA_HNA ||
          serviceAlert.loginBeamType === ServiceAlertType.HCX_HNA)
      ) {
        return generateHnaRestrictionAlertDialogComponent(serviceAlert, beams, map, visibleOverlaysObj);
      } else if (serviceAlert.loginBeamInCoverage) {
        return (
          <ServiceAlertDialog
            key={serviceAlert.timeSinceTakeOffMin}
            map={map}
            visible={visibleOverlaysObj[serviceAlert.id]}
            childComponent={
              <MarkerDialogContent
                title={'ENTER COVERAGE'}
                exit={'No Coverage'}
                enter={serviceAlert.loginBeamDisplayName}
                timeSinceTakeOffMin={serviceAlert.timeSinceTakeOffMin}
                userSetTakeoffTimeMin={serviceAlert.timeSinceUserSetTakeoffMin}
                serviceAlertEventType={ServiceAlertEventType.ENTER_COVERAGE}
              />
            }
            latLng={new google.maps.LatLng(serviceAlert.lat, serviceAlert.lon)}
            veritcalOffsetPx={124}
          />
        );
      } else {
        return (
          <ServiceAlertDialog
            key={serviceAlert.timeSinceTakeOffMin}
            map={map}
            visible={visibleOverlaysObj[serviceAlert.id]}
            childComponent={
              <MarkerDialogContent
                title={'ENTER NO COVERAGE'}
                exit={ serviceAlert.logoutBeamDisplayName }
                enter={'No Coverage'}
                timeSinceTakeOffMin={serviceAlert.timeSinceTakeOffMin}
                userSetTakeoffTimeMin={serviceAlert.timeSinceUserSetTakeoffMin}
                serviceAlertEventType={ServiceAlertEventType.ENTER_NO_COVERAGE}
              />
            }
            latLng={new google.maps.LatLng(serviceAlert.lat, serviceAlert.lon)}
            veritcalOffsetPx={124}
          />
        );
      }
    default:
      break;
  }
  return serviceAlertDialogComponent;
};
// Encode the URI for IE compatability
export const getMarkerImageForServiceAlert = (serviceAlert: ServiceAlert) => {
  let markerImage = undefined;
  switch (serviceAlert.type) {
    case 'HNA_ZONE':
    case 'HNA':
    case 'KA_HNA':
    case 'HCX_HNA':
    case 'NO_COVERAGE':
      const alertSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M0 0h24v24H0z" fill="none"/>
    <rect fill="#141D24" x="10" y="5" height="13" width="4">
    </rect>
    <path fill="#f0f0f0" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
</svg>
`;
      markerImage = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(alertSvg)}`;
      break;
    case 'HAND_OFF':
      const handoffSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path fill="none" d="M0 0h24v24H0V0z"/>
    <circle cx="12" cy="12" r="9" fill="#141D24"/>
    <path fill="#FFAF07" d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12s4.48 10 10 10 10-4.48 10-10zm-7-5.5l3.5 3.5-3.5 3.5V11h-4V9h4V6.5zm-6 11L5.5 14 9 10.5V13h4v2H9v2.5z"/>
</svg>
`;
      markerImage = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(handoffSvg)}`;
      break;
    default:
      break;
  }
  return markerImage;
};

export const getTakeoffMarkerSvg = () => {
  let svg = `
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="20" height="20"
     viewBox="0 0 32 32" >
    <defs>
        <path id="a" d="M0 0h24v24H0V0z"/>
    </defs>
    <clipPath id="b">
        <use xlink:href="#a" overflow="visible"/>
    </clipPath>
    <circle  fill="white" cx="16" cy="16" r="16">
    </circle>
    <g transform="translate(4, 4)">
    <path fill="#141D24" d="M2.5 19h19v2h-19zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 1.82 3.16.77 1.33 1.6-.43 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z"
          clip-path="url(#b)"/>
    <path clip-path="url(#b)" fill="none" d="M0 0h24v24H0V0z"/>
    </g>
</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

export const getLandingMarkerSvg = () => {
  let svg = `
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="20" height="20"
     viewBox="0 0 32 32" >
    <defs>
        <path id="a" d="M0 0h24v24H0V0z"/>
    </defs>
    <clipPath id="b">
        <use xlink:href="#a" overflow="visible"/>
    </clipPath>
    <circle  fill="white" cx="16" cy="16" r="16">
    </circle>
    <g transform="translate(4, 4)">
        <path fill="#141D24" d="M2.5 19h19v2h-19zm7.18-5.73l4.35 1.16 5.31 1.42c.8.21 1.62-.26 1.84-1.06.21-.8-.26-1.62-1.06-1.84l-5.31-1.42-2.76-9.02L10.12 2v8.28L5.15 8.95l-.93-2.32-1.45-.39v5.17l1.6.43 5.31 1.43z"
              clip-path="url(#b)"/>
        <path clip-path="url(#b)" fill="none" d="M0 0h24v24H0V0z"/>
    </g>
</svg>
`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};
